import { supabase } from './supabaseClient';
import { getCurrentCopy } from '../i18n/current';
import type {
  Organization,
  OrganizationWithRole,
  OrganizationMemberWithUser,
  OrganizationInvite,
  OrgMemberRole,
  ProjectMemberRole
} from '../types';

// ========= Organizações CRUD =========

export async function getUserOrganizations(userId: string): Promise<OrganizationWithRole[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      role,
      organization:organizations(*)
    `)
    .eq('user_id', userId);

  if (error) throw error;
  if (!data) return [];

  // Supabase retorna organization como objeto (inner join)
  type OrgMembershipRow = { role: OrgMemberRole; organization: Organization };
  const rows = data as unknown as OrgMembershipRow[];

  // Buscar contagem de membros por org
  const orgIds = rows.map(om => om.organization.id);

  const { data: counts } = await supabase
    .from('organization_members')
    .select('organization_id')
    .in('organization_id', orgIds);

  const memberCounts: Record<string, number> = {};
  if (counts) {
    for (const row of counts) {
      memberCounts[row.organization_id] = (memberCounts[row.organization_id] || 0) + 1;
    }
  }

  return rows.map(om => ({
    ...om.organization,
    userRole: om.role,
    memberCount: memberCounts[om.organization.id] || 0
  }));
}

export async function createOrganization(
  name: string,
  slug: string,
  description?: string
): Promise<Organization> {
  const { data, error } = await supabase.rpc('create_organization', {
    p_name: name,
    p_slug: slug,
    p_description: description || ''
  });

  if (error) throw error;

  // Buscar org criada
  const { data: org, error: fetchError } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', data)
    .single();

  if (fetchError) throw fetchError;
  return org as Organization;
}

export async function updateOrganization(
  id: string,
  updates: Partial<Pick<Organization, 'name' | 'description' | 'avatar_url'>>
): Promise<Organization> {
  const { data, error } = await supabase
    .from('organizations')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Organization;
}

export async function deleteOrganization(id: string): Promise<void> {
  const { error } = await supabase
    .from('organizations')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ========= Membros =========

export async function getMembers(orgId: string): Promise<OrganizationMemberWithUser[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      *,
      user:profiles!fk_org_members_user_profile(id, email, full_name, avatar_url)
    `)
    .eq('organization_id', orgId)
    .order('role', { ascending: true });

  if (error) throw error;
  return (data || []) as OrganizationMemberWithUser[];
}

export async function removeMember(orgId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('organization_id', orgId)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function updateMemberRole(
  orgId: string,
  userId: string,
  newRole: OrgMemberRole
): Promise<void> {
  const { error } = await supabase
    .from('organization_members')
    .update({ role: newRole })
    .eq('organization_id', orgId)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function transferOwnership(orgId: string, newOwnerId: string): Promise<void> {
  const { data, error } = await supabase.rpc('transfer_org_ownership', {
    p_org_id: orgId,
    p_new_owner_id: newOwnerId
  });

  if (error) throw error;
  const result = data as { error?: string };
  if (result?.error) throw new Error(result.error);
}

// ========= Convites =========

export async function getInvites(orgId: string): Promise<OrganizationInvite[]> {
  const { data, error } = await supabase
    .from('organization_invites')
    .select('*')
    .eq('organization_id', orgId)
    .is('accepted_at', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as OrganizationInvite[];
}

export async function inviteMember(
  orgId: string,
  email: string,
  role: OrgMemberRole,
  invitedBy: string
): Promise<{ invite: OrganizationInvite; emailSent: boolean }> {
  const { data, error } = await supabase
    .from('organization_invites')
    .insert({
      organization_id: orgId,
      email: email.toLowerCase().trim(),
      role,
      invited_by: invitedBy
    })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error(getCurrentCopy().common.serviceErrors.invitePending);
    }
    throw error;
  }

  const invite = data as OrganizationInvite;
  const emailSent = await sendOrgInviteEmail(invite.id);
  return { invite, emailSent };
}

async function sendOrgInviteEmail(inviteId: string): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke('send-org-invite-email', {
    body: { invite_id: inviteId }
  });
  if (error) {
    console.error('[orgService] Edge Function send-org-invite-email falhou:', error);
    return false;
  }
  if (data?.skipped) {
    console.warn('[orgService] Envio de email pulado:', data.reason);
    return false;
  }
  if (data?.error) {
    console.error('[orgService] Erro retornado pelo provedor:', data.error);
    return false;
  }
  return true;
}

export async function cancelInvite(inviteId: string): Promise<void> {
  const { error } = await supabase
    .from('organization_invites')
    .delete()
    .eq('id', inviteId);

  if (error) throw error;
}

export async function acceptInvite(token: string): Promise<{ organizationId: string; alreadyMember?: boolean }> {
  const { data, error } = await supabase.rpc('accept_org_invite', {
    p_token: token
  });

  if (error) throw error;

  const result = data as { success?: boolean; error?: string; organization_id?: string; already_member?: boolean };
  if (result.error) {
    throw new Error(result.error);
  }

  return {
    organizationId: result.organization_id!,
    alreadyMember: result.already_member || false
  };
}

export async function declineInvite(token: string): Promise<void> {
  const { error } = await supabase.rpc('decline_org_invite', {
    p_token: token
  });

  if (error) throw error;
}

export async function getPendingInvitesForUser(email: string): Promise<(OrganizationInvite & { organization?: Organization })[]> {
  const { data, error } = await supabase
    .from('organization_invites')
    .select(`
      *,
      organization:organizations(id, name, slug, avatar_url)
    `)
    .eq('email', email.toLowerCase())
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString());

  if (error) throw error;
  return (data || []) as (OrganizationInvite & { organization?: Organization })[];
}

// ========= Projetos na Organização =========

export async function moveProjectToOrg(
  projectId: string,
  orgId: string | null
): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ organization_id: orgId, updated_at: new Date().toISOString() })
    .eq('id', projectId);

  if (error) throw error;
}

export async function shareProjectWithOrgMembers(
  projectId: string,
  orgId: string,
  defaultRole: ProjectMemberRole,
  invitedBy: string
): Promise<number> {
  // Usar RPC com SECURITY DEFINER para bypass de RLS
  // (inserir project_members com user_id de outros usuários requer privilégio)
  const { data, error } = await supabase.rpc('share_project_with_org_members', {
    p_project_id: projectId,
    p_org_id: orgId,
    p_default_role: defaultRole,
    p_invited_by: invitedBy
  });

  if (error) throw error;

  const sharedCount = (data as number) || 0;

  // Auto-criar team_members com linked_user_id (híbrido)
  if (sharedCount > 0) {
    try {
      await supabase.rpc('sync_org_members_as_team_members', {
        p_project_id: projectId,
        p_org_id: orgId
      });
    } catch (err) {
      console.warn('[orgService] Erro ao sincronizar team_members:', err);
    }
  }

  return sharedCount;
}

// ========= Slug Helpers =========

export async function isSlugAvailable(slug: string): Promise<boolean> {
  const { data } = await supabase
    .from('organizations')
    .select('id')
    .eq('slug', slug)
    .maybeSingle();

  return !data;
}

export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .replace(/[^a-z0-9]+/g, '-')    // substitui caracteres especiais por hífen
    .replace(/^-+|-+$/g, '')        // remove hífens do início/fim
    .substring(0, 50);
}
